import { useEffect, useState } from 'react';
import { api } from '../../api/client';
import { initials } from '../../lib/format';
import { useAuth } from '../../store/auth';

/** One fetched photo per version: the file needs the sign-in token, so it is loaded as a blob, not by <img src>. */
const cache = new Map<string, Promise<string | null>>();

function loadPhoto(version: string): Promise<string | null> {
  let p = cache.get(version);
  if (!p) {
    p = api
      .get('/auth/me/photo', { responseType: 'blob' })
      .then((r) => URL.createObjectURL(r.data as Blob))
      .catch(() => null);
    cache.set(version, p);
  }
  return p;
}

/** The signed-in user's photo as an object URL, or null when there is none (or it is still loading). */
export function useMyPhoto(): string | null {
  const version = useAuth((s) => s.user?.photoUpdatedAt ?? null);
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    setUrl(null);
    if (version) void loadPhoto(version).then((u) => live && setUrl(u));
    return () => {
      live = false;
    };
  }, [version]);
  return url;
}

/** The profile picture, falling back to initials on the accent circle. */
export function Avatar({ className = '', size, preview }: { className?: string; size?: number; preview?: string | null }) {
  const name = useAuth((s) => s.user?.name ?? '');
  const photo = useMyPhoto();
  const src = preview ?? photo;
  return (
    <div className={`avatar ${className}`} style={size ? { width: size, height: size, fontSize: size * 0.3 } : undefined} title={name}>
      {src ? <img src={src} alt="" /> : initials(name)}
    </div>
  );
}
